import { BASE_SUIT, type SuitStats } from '../sim/suit';

/**
 * Permanent suit upgrades bought in the lab with rare materials (GDD: Upgrades). Five tracks.
 * The Earth tutorial gives the first Shielding level (the Heat Shield); each more makes the next
 * radiation tier safe. Arsenal levels are slots for the weapon mods found in a run.
 */

export const TRACK_IDS = ['armor', 'reactor', 'thrusters', 'shielding', 'arsenal'] as const;
export type TrackId = (typeof TRACK_IDS)[number];

export interface Track {
  readonly id: TrackId;
  readonly name: string;
  /** What one level does, for the lab's panel. */
  readonly effect: string;
  readonly levels: number;
}

export const TRACKS: readonly Track[] = [
  { id: 'armor', name: 'ARMOR', effect: '+1 ARMOR PLATE', levels: 5 },
  { id: 'reactor', name: 'REACTOR', effect: '+20 STARLIGHT, FASTER REFILL', levels: 5 },
  { id: 'thrusters', name: 'THRUSTERS', effect: 'JETPACK BURNS LESS', levels: 5 },
  { id: 'shielding', name: 'SHIELDING', effect: 'NEXT RADIATION TIER SAFE', levels: 5 },
  { id: 'arsenal', name: 'ARSENAL', effect: 'A WEAPON MOD SLOT', levels: 3 },
];

/** Materials the Ranger leaves when beaten (shards in rooms and rocks give one each). */
export const BOSS_MATERIALS = 6;

/** Materials for each level, from the first. */
export const LEVEL_COST = [4, 8, 12, 18, 25] as const;

export type UpgradeLevels = Readonly<Record<TrackId, number>>;

export const NO_UPGRADES: UpgradeLevels = {
  armor: 0,
  reactor: 0,
  thrusters: 0,
  shielding: 0,
  arsenal: 0,
};

/** The cost of the next level of a track, or null when it is maxed. */
export function nextCost(track: Track, level: number): number | null {
  if (level >= track.levels) return null;
  return LEVEL_COST[level] ?? null;
}

export type BuyCheck = 'ok' | 'maxed' | 'short';

export function canBuy(track: Track, level: number, materials: number): BuyCheck {
  const cost = nextCost(track, level);
  if (cost === null) return 'maxed';
  return materials >= cost ? 'ok' : 'short';
}

/** Buys the next level of a track on a save draft. Returns false (changing nothing) if it can't. */
export function buyUpgrade(
  draft: { materials: number; upgrades: Record<TrackId, number> },
  id: TrackId,
): boolean {
  const track = TRACKS.find((t) => t.id === id);
  if (!track) return false;
  const level = draft.upgrades[id];
  const cost = nextCost(track, level);
  if (cost === null || canBuy(track, level, draft.materials) !== 'ok') return false;
  draft.materials -= cost;
  draft.upgrades[id] = level + 1;
  return true;
}

/** The suit a run starts with, from the levels bought. */
export function suitStats(levels: UpgradeLevels): SuitStats {
  return {
    armorMax: BASE_SUIT.armorMax + levels.armor,
    starlightMax: BASE_SUIT.starlightMax + 20 * levels.reactor,
    regenPerTickGrounded: BASE_SUIT.regenPerTickGrounded * (1 + 0.25 * levels.reactor),
    jetDrainPerTick: BASE_SUIT.jetDrainPerTick * (1 - 0.12 * levels.thrusters),
    shielding: BASE_SUIT.shielding + levels.shielding,
    modSlots: BASE_SUIT.modSlots + levels.arsenal,
  };
}
